import "server-only"
import axios from "axios"
import { env } from "@/env"

interface BaseFetchOptions extends RequestInit {
  headers?: Record<string, string>
  next?: NextFetchRequestConfig
}

interface FetchApiResponse<T = unknown> {
  data: T
  headers?: Headers
}

// Function overloads for fetchWpApi
export async function fetchWpApi<T = unknown>(
  endpoint: string,
  options: BaseFetchOptions,
  returnHeaders: true
): Promise<FetchApiResponse<T>>

export async function fetchWpApi<T = unknown>(
  endpoint: string,
  options?: BaseFetchOptions,
  returnHeaders?: false
): Promise<T>

export async function fetchWpApi<T = unknown>(
  endpoint: string,
  options: BaseFetchOptions = {},
  returnHeaders = false
): Promise<T | FetchApiResponse<T>> {
  const apiUrl = env.WP_API_URL

  if (!apiUrl) {
    throw new Error("API URL is not configured in environment variables.")
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...options.headers,
  }

  if (endpoint.includes("/wc/v3/")) {
    // Para endpoints do WooCommerce, use as chaves da API
    const consumerKey = env.WOOCOMMERCE_CONSUMER_KEY
    const consumerSecret = env.WOOCOMMERCE_CONSUMER_SECRET

    if (!(consumerKey && consumerSecret)) {
      throw new Error(
        "WooCommerce API Keys are not configured in server environment variables."
      )
    }

    const credentials = `${consumerKey}:${consumerSecret}`
    const encodedCredentials = Buffer.from(credentials).toString("base64")
    headers.Authorization = `Basic ${encodedCredentials}`
  }

  const response = await fetch(`${apiUrl}${endpoint}`, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error(`API Error for endpoint ${endpoint}:`, errorText)
    throw new Error(
      `API request failed with status ${response.status}: ${response.statusText}`
    )
  }

  const data = (await response.json()) as T

  if (returnHeaders) {
    return {
      data,
      headers: response.headers,
    }
  }

  return data
}

if (!(env.WOOCOMMERCE_CONSUMER_KEY && env.WOOCOMMERCE_CONSUMER_SECRET)) {
  throw new Error("WooCommerce API keys are not configured.")
}

const wcAuth = Buffer.from(
  `${env.WOOCOMMERCE_CONSUMER_KEY}:${env.WOOCOMMERCE_CONSUMER_SECRET}`
).toString("base64")

export const wooCommerceApi = axios.create({
  adapter: "fetch",
  baseURL: `${env.WP_API_URL}/wc/v3`,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Basic ${wcAuth}`,
  },
})

export const dokanApi = axios.create({
  adapter: "fetch",
  baseURL: `${env.WP_API_URL}/dokan/v1`,
  timeout: 15_000,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Basic ${wcAuth}`,
  },
})

export const publicDokanApi = axios.create({
  adapter: "fetch",
  baseURL: `${env.WP_API_URL}/dokan/v1`,
  timeout: 15_000,
  headers: {
    "Content-Type": "application/json",
  },
})

export const wordPressApi = axios.create({
  adapter: "fetch",
  baseURL: env.WP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
})
